import type {
  ChatMessage,
  Conversation,
  StreamCallback,
  ChatAttachment,
  ToolResult,
  ToolName,
} from './types';
import { checkSafety } from './safetyFilter';
import { classifyIntent } from './intentRouter';
import { renderTemplate } from './responseTemplates';
import { createMockStream } from './mockStreamEmitter';
import { executeTools } from './mockToolExecutor';
import { searchKB } from './knowledgeBase';
import { logChatEvent } from './observability';

export interface ProcessMessageOptions {
  onChunk: StreamCallback;
  attachments?: ChatAttachment[];
  route?: string;
  mode?: 'support' | 'analysis';
}

export class ChatEngine {
  async processMessage(
    userContent: string,
    conversation: Conversation,
    options: ProcessMessageOptions,
  ): Promise<ChatMessage> {
    const startTime = Date.now();

    // 1. Safety check
    const safetyResult = checkSafety(userContent);
    const sanitizedInput = safetyResult.redactedInput ?? userContent;

    if (!safetyResult.safe && safetyResult.redactedInput) {
      // Warn user about redacted content, but still process the sanitized version
    }

    // 2. Intent routing
    const intents = classifyIntent(sanitizedInput, options.attachments);
    const topIntent = intents[0];

    // 3. Execute mock tools if intent requires them
    let toolResults: ToolResult[] = [];
    toolResults = executeTools(topIntent, options.route);

    // 4. KB search fallback: if intent is kb_search or low confidence on primary
    if (topIntent.intent === 'kb_search' || (topIntent.confidence < 0.5 && topIntent.intent !== 'fallback')) {
      const kbResults = searchKB(sanitizedInput);
      if (kbResults.length > 0) {
        toolResults.push({
          tool: 'mockSearchKB' as ToolName,
          executedAt: new Date().toISOString(),
          data: { articles: kbResults },
        });
      }
    }

    // 5. Render response template with tool results and context
    const rendered = renderTemplate(topIntent.intent, {
      toolResults,
      entities: topIntent.extractedEntities as Record<string, string | undefined>,
      attachments: options.attachments,
    });

    // 6. Stream response
    const streamStart = Date.now();
    const messageId = `asst_${Date.now()}`;

    return new Promise<ChatMessage>((resolve) => {
      const streamOptions = options.mode === 'analysis'
        ? { wordsPerChunk: 2, baseDelayMs: 25, jitterMs: 15 }
        : { wordsPerChunk: 4, baseDelayMs: 30, jitterMs: 20 };

      createMockStream(rendered, (chunk) => {
        options.onChunk(chunk);
        if (chunk.done) {
          const message: ChatMessage = {
            id: messageId,
            role: 'assistant',
            content: rendered,
            timestamp: new Date().toISOString(),
            status: 'complete',
            intent: topIntent.intent,
            toolResults: toolResults.length > 0 ? toolResults : undefined,
            metadata: {
              streamDurationMs: Date.now() - streamStart,
              tokensEstimate: Math.ceil(rendered.length / 4),
              fallback: topIntent.intent === 'fallback',
            },
          };

          // Log for observability
          logChatEvent({
            conversationId: conversation.id,
            messageId,
            intent: topIntent.intent,
            confidence: topIntent.confidence,
            toolsCalled: toolResults.map(r => r.tool),
            fallback: topIntent.intent === 'fallback',
            durationMs: Date.now() - startTime,
            timestamp: new Date().toISOString(),
          });

          resolve(message);
        }
      }, streamOptions);
    });
  }
}

// Singleton instance
export const chatEngine = new ChatEngine();
